import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { Question } from '@/features/survey/survey-types'
import { useFormContext } from 'react-hook-form'
import { useAppSelector } from '@/app/hooks'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '@/components/atoms/error-message'

type Props = {
  question: Question
  index: number
}

export const SurveyNpsForm = ({ question, index }: Props) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const { t } = useTranslation()
  const design = useAppSelector(state => state.design)

  // エラーメッセージの取得
  const errorMessage = errors.answers?.[index]?.answerNumber?.message as string | undefined

  const scoreOptionStyle = useMemo(
    () =>
      css({
        width: '100%',
        aspectRatio: '9 / 16',
        border: `1.5px solid ${design?.theme.brandColor}`,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: design?.background.cardColor,
        fontWeight: 'bold',
        color: design?.theme.brandColor,
        transition: 'all 0.2s ease',
        marginBottom: '8px',
        fontSize: 'clamp(14px, 8cqw, 28px)',
      }),
    [design],
  )

  const checkedStyle = useMemo(
    () =>
      css({
        'input[type="radio"]:checked + div': {
          backgroundColor: design?.theme.brandColor,
          color: design?.background.cardColor,
        },
      }),
    [design],
  )

  const edgeLabelTextStyle = useMemo(
    () =>
      css({
        fontSize: '12px',
        color: design?.theme.brandColor,
      }),
    [design],
  )

  const gridStyle = css({
    display: 'grid',
    gridTemplateColumns: `repeat(11, 1fr)`,
    gap: 'clamp(8px, 2vw, 24px)',
    justifyItems: 'stretch',
    width: '100%',
  })
  return (
    <div css={wrapperStyle}>
      <div css={[gridStyle, checkedStyle]}>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
          <label key={score} css={labelStyle}>
            <input
              type="radio"
              {...register(`answers.${index}.answerNumber`, {
                required: question.isRequired ? t('This field is required') : false,
              })}
              value={score}
              css={inputStyle}
            />
            <div css={scoreOptionStyle}>{score}</div>
          </label>
        ))}
      </div>
      <div css={edgeLabelsRowStyle}>
        <span css={edgeLabelTextStyle}>全くおすすめしない</span>
        <span css={edgeLabelTextStyle}>ぜひおすすめしたい</span>
      </div>
      {/* エラーメッセージの表示 */}
      {errorMessage && (
        <div css={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
          <ErrorMessage text={errorMessage} />
        </div>
      )}
    </div>
  )
}

const wrapperStyle = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
})

const labelStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
  width: '100%',
  containerType: 'inline-size',
})

const inputStyle = css({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
})

const edgeLabelsRowStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
})
